/**
 * Κρατήσεις πάνω στην κάτοψη του ταμείου (Xentrix Sala ή ο παλιός διαχειριστής).
 *
 * Ένα τραπέζι φαίνεται «κρατημένο» μόνο όταν η κράτηση πλησιάζει (2 ώρες πριν) ή
 * αργεί λίγο — όχι από το πρωί για μια κράτηση των 22:00, γιατί τότε ο σερβιτόρος
 * δεν θα έδινε το τραπέζι σε walk-in που θα έφευγε πολύ νωρίτερα.
 */

export interface FloorReservation {
  id: number;
  table_id: number | null;
  extra_table_ids?: number[] | null;
  customer_name: string;
  reservation_time: string;
  party_size: number;
  status: string;
  session_status?: string | null;
  notes?: string | null;
  tags?: string[] | null;
  allergies?: string | null;
  guest_allergies?: string | null;
  guest_is_vip?: boolean | null;
  table_number?: string;
}

export interface TableReservations {
  /** η επόμενη κράτηση που «πιάνει» το (ελεύθερο) τραπέζι */
  next?: FloorReservation;
  /** η παρέα της κράτησης που κάθεται τώρα στο τραπέζι */
  seated?: FloorReservation;
}

/** Πόσα λεπτά πριν από την ώρα της το τραπέζι φαίνεται κρατημένο. */
export const RESERVED_AHEAD_MIN = 120;
/** Πόσα λεπτά μετά την ώρα της μια κράτηση που δεν ήρθε κρατά ακόμα το τραπέζι. */
export const LATE_GRACE_MIN = 60;

const WAITING = ['pending', 'confirmed', 'arrived'];
const OPEN_BILL = ['open', 'receipt_printed', 'invoice_printed'];

/** «20:30» / «20:30:00» → λεπτά από τα μεσάνυχτα. */
export const toMinutes = (time: string): number => {
  const [h, m] = String(time || '').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** Τα λεπτά της ώρας Ελλάδας — το ταμείο μπορεί να έχει λάθος ζώνη ώρας. */
export function athensMinutes(now: Date | number = Date.now()): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    // hourCycle, όχι hour12:false (κύκλος h24 → «24» μετά τα μεσάνυχτα)· το % 24 μένει ως δίχτυ.
    timeZone: 'Europe/Athens', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(now));
  const h = Number(parts.find(p => p.type === 'hour')?.value) % 24;
  const m = Number(parts.find(p => p.type === 'minute')?.value);
  return h * 60 + m;
}

export const tablesOf = (r: FloorReservation): number[] =>
  [r.table_id, ...(r.extra_table_ids || [])].filter((id): id is number => id != null);

export const allergiesOf = (r: FloorReservation): string =>
  (r.allergies || r.guest_allergies || '').trim();

/** Περιμένει ακόμα (δεν ήρθε/δεν κάθισε) και η ώρα της είναι κοντά. */
export function isDue(r: FloorReservation, nowMin: number, ahead = RESERVED_AHEAD_MIN): boolean {
  if (!WAITING.includes(r.status)) return false;
  const diff = toMinutes(r.reservation_time) - nowMin;
  if (r.status === 'arrived') return true; // είναι ήδη εδώ και περιμένει τραπέζι
  return diff <= ahead && diff >= -LATE_GRACE_MIN;
}

/** Κάθεται τώρα: «κάθισε» και ο λογαριασμός του ταμείου είναι ακόμα ανοιχτός. */
export const isSeated = (r: FloorReservation): boolean =>
  r.status === 'seated' && (!r.session_status || OPEN_BILL.includes(r.session_status));

export function floorReservations(list: FloorReservation[], nowMin: number, ahead = RESERVED_AHEAD_MIN): Map<number, TableReservations> {
  const out = new Map<number, TableReservations>();
  const sorted = [...list].sort((a, b) => toMinutes(a.reservation_time) - toMinutes(b.reservation_time));
  for (const r of sorted) {
    const seated = isSeated(r);
    const due = !seated && isDue(r, nowMin, ahead);
    if (!seated && !due) continue;
    for (const id of tablesOf(r)) {
      const cur = out.get(id) || {};
      if (seated && !cur.seated) cur.seated = r;
      if (due && !cur.next) cur.next = r;
      out.set(id, cur);
    }
  }
  return out;
}

/** Η λίστα της κεφαλίδας: όσοι έρχονται από εδώ και πέρα (ή αργούν λίγο), με τη σειρά. */
export function upcomingList(list: FloorReservation[], nowMin: number): FloorReservation[] {
  return list
    .filter(r => WAITING.includes(r.status) && (r.status === 'arrived' || toMinutes(r.reservation_time) >= nowMin - LATE_GRACE_MIN))
    .sort((a, b) => toMinutes(a.reservation_time) - toMinutes(b.reservation_time));
}
